import {Alert, Button, Group, Text} from '@mantine/core'
import {useNavigate} from '@tanstack/react-router'

import {pickAddJobSource} from 'src/jobs/order-plus/pick-source-job'
import type {JobLocation} from 'src/jobs/types'
import {useProject} from 'src/projects/use-projects'
import {useCustomerWork} from 'src/work/use-work'
import {
    openWorkAtSameSite,
    othersOpenWorkAtSameSite,
    othersOpenWorkMessage,
    workOptionLabel,
} from 'src/work/work-options'

type OpenOrderNoticeProps = {
    customerId: string | null
    locations: Pick<JobLocation, 'name' | 'fromSavedLocationId'>[]
}

export function OpenOrderNotice({customerId, locations}: OpenOrderNoticeProps) {
    const navigate = useNavigate()
    const {data: work = []} = useCustomerWork(customerId)
    const match = locations.length > 0 ? openWorkAtSameSite(work, locations) : null
    const others = locations.length > 0 ? othersOpenWorkAtSameSite(work, locations) : []
    const project = useProject(match?.projectId ?? '', {enabled: false})
    if (!match && others.length === 0) {
        return null
    }

    async function handleAddToOrder(projectId: string) {
        const {data} = await project.refetch()
        if (data) {
            await navigate({
                to: '/projects/$projectId/jobs/add',
                params: {projectId},
                search: {from: pickAddJobSource(data.jobs)?.id},
            })
        }
    }

    return (
        <Alert
            color="blue"
            variant="light"
            title="Open order at this site"
            data-testid="open-order"
        >
            {match ? (
                <Group justify="space-between" gap="sm">
                    <Text size="sm">{workOptionLabel(match)}. Add this job to that order?</Text>
                    <Button
                        size="compact-sm"
                        variant="light"
                        loading={project.isFetching}
                        onClick={() => void handleAddToOrder(match.projectId)}
                    >
                        Add to that order
                    </Button>
                </Group>
            ) : null}
            {others.length > 0 ? (
                <Text size="sm" mt={match ? 'xs' : 0} data-testid="open-order-other-rep">
                    {othersOpenWorkMessage(others, match != null)}
                </Text>
            ) : null}
            {project.isError ? (
                <Text size="sm" c="red" mt="xs">
                    Could not open order
                </Text>
            ) : null}
        </Alert>
    )
}
